import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { PageWrapper, PageHeader, PageTitle, Card, CardTitle, Badge, EmptyState } from '../CompanyPage/CompanyPage.styles';
import {
  PageSubtitle, HeaderActions, DocRow, DocMain, DocTitleRow, DocSummary, DocMeta, DocActions,
  StatusBadge, PrimaryButton, GhostButton, DangerButton, ErrorText, Notice, ErrorBanner, QuestionCountSelect,
  Layout, Rail, RailItem, RailCount, RailActions, Content, Toolbar, SearchInput, StatusLine,
  GroupHeading, GroupToggle, SelectBar, SelectCheckbox, MoveSelect,
} from './DocumentLibraryPage.styles';
import AddDocumentModal from './AddDocumentModal';
import { expandQuery, textMatches } from '../../utils/searchTerms';

const STATUS_LABELS = {
  ready: 'Ready', queued: 'Waiting its turn', processing: 'Preparing questions', uploading: 'Uploading', error: 'Needs attention',
};

// Rejections whose file the server removed: retrying can never succeed and
// there is nothing left to view, so only Remove is offered.
const FILE_REMOVED_ERROR = /already in the library|doesn't appear to relate to|couldn't find any readable text|couldn't open this file/i;
const fileRemoved = (doc) => doc.hasError && FILE_REMOVED_ERROR.test(doc.error || '');

// Mirrors the server (download-url viewModeFor): only PDFs and text open in a
// tab; Word and PowerPoint download, because Edge's built-in Office viewer
// showed a 404 for them (Ryan, 2026-10-05). Decided before the request, since
// a tab has to be opened inside the click to get past pop-up blockers.
const OPENS_IN_BROWSER = new Set(['pdf', 'txt', 'md']);
// A browser with PDF display turned off (Chrome's "Download PDFs" setting,
// Firefox with its viewer off, older Android Chrome) says so here; a PDF then
// downloads in place instead of leaving a blank tab behind. Browsers without
// the property are assumed to show PDFs, which all current ones do.
const canShowPdf = () => typeof navigator === 'undefined' || navigator.pdfViewerEnabled !== false;
const opensInBrowser = (doc) => {
  const extension = String(doc.extension || '').toLowerCase();
  if (extension === 'pdf') return canShowPdf();
  return OPENS_IN_BROWSER.has(extension);
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatSize = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// A library with folders, or with this many documents, gets the organised view
// (folder rail, grouping by type, search). Smaller ones keep the flat list.
export const RAIL_THRESHOLD = 10;
export const NOT_READY_GROUP = 'Not ready yet';

// Groups for the organised view: documents Murphy has not typed yet first,
// then one group per document type, "Other" last. Order inside a group is the
// list order (newest first).
export const groupDocuments = (docs) => {
  const groups = new Map();
  docs.forEach((doc) => {
    const key = (!doc.isReady && !doc.documentType) ? NOT_READY_GROUP : (doc.documentType || 'Other');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(doc);
  });
  const rank = (k) => (k === NOT_READY_GROUP ? 0 : k === 'Other' ? 2 : 1);
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([label, items]) => ({ label, docs: items }));
};

// Synonyms and word forms (utils/searchTerms): "cost" finds the fee schedule.
// `query` may be the typed text or an already expanded query.
export const matchesSearch = (doc, query) => {
  const expanded = typeof query === 'string' ? expandQuery(query) : (query || []);
  return textMatches([doc.title, doc.fileName, doc.documentSummary].map((v) => String(v || '')).join(' '), expanded);
};

// Company-wide Document Defense Library. Everyone in the company can browse,
// read and practice; adding, filing, renaming and removing are for content
// editors (User.canEditCompanyContent, mirrored by the server's
// canEditCompanyContent). Folders organise; they never restrict who sees what.
const DocumentLibraryPage = observer(({ documentLibraryStore, meetingStore, companyStore, currentUser, history }) => {
  const company = companyStore?.company;
  const companyId = company?.id;
  const canCurate = !!currentUser?.canEditCompanyContent;

  const [showAdd, setShowAdd] = useState(false);
  const [pageError, setPageError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [questionCount, setQuestionCount] = useState(5);
  const [selection, setSelection] = useState('all'); // 'all' | 'unfiled' | folderId
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkTarget, setBulkTarget] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setPageError('');
      await documentLibraryStore.load(companyId);
    } catch (e) {
      setPageError(e?.message || 'Could not load the Document Library.');
    }
  }, [companyId, documentLibraryStore]);

  useEffect(() => { load(); }, [load]);

  // The list is loaded once, so a tab left open while a curator removed or
  // replaced a document kept showing it, and View or Practice on that entry
  // answered "not found" (Ryan, 2026-10-05). Coming back to the tab refreshes
  // the list.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // A 404 on a document the page still lists means someone removed or
  // replaced it since the list loaded: refresh, and say so plainly.
  const isGone = (e) => e?.response?.status === 404;
  const showGone = async () => {
    await load();
    setPageError('That document is no longer in the library (it was removed or replaced). The list has been refreshed.');
  };

  // While anything is queued or being prepared, reload the list every few
  // seconds: one request per tick however many documents are busy (a bulk
  // add used to start one poller per document).
  const busyCount = documentLibraryStore.busyDocuments.length;
  useEffect(() => {
    if (!busyCount || !companyId) return undefined;
    const timer = setInterval(() => {
      documentLibraryStore.load(companyId).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [busyCount, companyId, documentLibraryStore]);

  const practice = async (doc) => {
    setPageError('');
    setBusyId(doc.documentId);
    try {
      const payload = await documentLibraryStore.startSession(companyId, doc.documentId, questionCount);
      meetingStore.loadReadyQASession(payload);
      history.push('/record-presentation/document-defense');
    } catch (e) {
      setBusyId(null);
      if (isGone(e)) { await showGone(); return; }
      setPageError(e?.message || 'Could not start the practice session.');
    }
  };

  const view = async (doc) => {
    setPageError('');
    // Open the tab synchronously, still inside the click, so pop-up blockers
    // allow it; then point it at the presigned URL once that arrives. A tab
    // opened after the await would be blocked by most browsers. A download
    // needs no tab: the link is signed as an attachment, so the page stays put.
    const inBrowser = opensInBrowser(doc);
    const tab = inBrowser ? window.open('', '_blank') : null;
    try {
      const url = await documentLibraryStore.getDownloadUrl(companyId, doc.documentId, { download: !inBrowser });
      if (!url) throw new Error('No download link was returned.');
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (e) {
      if (tab) tab.close();
      if (isGone(e)) { await showGone(); return; }
      setPageError(e?.message || 'Could not open the document.');
    }
  };

  const remove = async (doc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${doc.title}" from the library for everyone in ${company?.name || 'the company'}?`)) return;
    setPageError('');
    try {
      await documentLibraryStore.removeDocument(companyId, doc.documentId);
    } catch (e) {
      // Already gone (someone else removed it): the outcome the curator wanted.
      if (isGone(e)) { await load(); return; }
      setPageError(e?.message || 'Could not remove the document.');
    }
  };

  const retry = async (doc) => {
    setPageError('');
    setBusyId(doc.documentId);
    try {
      await documentLibraryStore.retryProcessing(companyId, doc.documentId);
    } catch (e) {
      if (isGone(e)) { await showGone(); return; }
      setPageError(e?.message || 'Could not retry processing.');
    } finally {
      setBusyId(null);
    }
  };

  const rename = async (doc) => {
    // eslint-disable-next-line no-alert
    const title = window.prompt('New title', doc.title);
    if (!title || title.trim() === doc.title) return;
    setPageError('');
    try {
      await documentLibraryStore.rename(companyId, doc.documentId, title.trim());
    } catch (e) {
      if (isGone(e)) { await showGone(); return; }
      setPageError(e?.message || 'Could not rename the document.');
    }
  };

  const moveOne = async (doc, folderId) => {
    setPageError('');
    try {
      await documentLibraryStore.moveDocuments(companyId, [doc.documentId], folderId || null);
    } catch (e) {
      if (isGone(e)) { await showGone(); return; }
      setPageError(e?.message || 'Could not move the document.');
    }
  };

  const moveSelected = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setPageError('');
    try {
      const res = await documentLibraryStore.moveDocuments(companyId, ids, bulkTarget || null);
      setSelectedIds(new Set());
      setSelectMode(false);
      if (res?.missing?.length) await showGone();
    } catch (e) {
      setPageError(e?.message || 'Could not move the documents.');
    }
  };

  const newFolder = async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('Folder name');
    if (!name || !name.trim()) return;
    setPageError('');
    try {
      const folder = await documentLibraryStore.createFolder(companyId, name.trim());
      if (folder?.folderId) setSelection(folder.folderId);
    } catch (e) {
      setPageError(e?.message || 'Could not create the folder.');
    }
  };

  const renameFolder = async (folder) => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('Folder name', folder.name);
    if (!name || name.trim() === folder.name) return;
    setPageError('');
    try {
      await documentLibraryStore.renameFolder(companyId, folder.folderId, name.trim());
    } catch (e) {
      setPageError(e?.message || 'Could not rename the folder.');
    }
  };

  const deleteFolder = async (folder) => {
    const count = documentLibraryStore.folderCount(folder.folderId);
    const consequence = count === 0 ? '' : ` Its ${count === 1 ? 'document moves' : `${count} documents move`} to Unfiled; nothing is deleted.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the folder "${folder.name}"?${consequence}`)) return;
    setPageError('');
    try {
      await documentLibraryStore.deleteFolder(companyId, folder.folderId);
      setSelection('all');
    } catch (e) {
      setPageError(e?.message || 'Could not delete the folder.');
    }
  };

  const onAdd = async (items, folderId) => {
    await documentLibraryStore.addDocuments(companyId, items, { folderId });
  };

  const closeAdd = () => {
    if (documentLibraryStore.uploading && documentLibraryStore.uploadsPending > 0) return;
    setShowAdd(false);
    documentLibraryStore.clearUploads();
  };

  const toggleGroup = (label) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const toggleSelected = (documentId) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(documentId)) next.delete(documentId); else next.add(documentId);
    return next;
  });

  const documents = documentLibraryStore.documents;
  const folders = documentLibraryStore.folders;
  // The store keeps the list between visits, so a return visit paints at once
  // and refreshes underneath. Only the first visit has nothing to show, and
  // until its answer arrives that is "loading", never "no documents yet".
  const hasLoaded = documentLibraryStore.loadedForCompanyId === companyId;
  const organised = folders.length > 0 || documents.length >= RAIL_THRESHOLD;

  // Curators always see Unfiled: it is the prompt to file what is waiting. Reps
  // see it as "Other documents", and only while something is in it (Toby,
  // 2026-10-16).
  const unfiledCount = documentLibraryStore.unfiledCount;
  const showUnfiled = canCurate || unfiledCount > 0;
  const unfiledLabel = canCurate ? 'Unfiled' : 'Other documents';

  // A folder deleted elsewhere (or by us), or an Unfiled entry that emptied
  // out from under a rep, must not leave an empty selection.
  const selectedFolder = selection !== 'all' && selection !== 'unfiled' ? documentLibraryStore.folderById(selection) : null;
  const effectiveSelection = (selection === 'all' || (selection === 'unfiled' && showUnfiled) || selectedFolder) ? selection : 'all';

  // Computed on every render on purpose: the store's list is a MobX array
  // that changes in place (a reload, a status flip), so a memo keyed on it
  // would keep serving the old rows.
  const inSelection = organised ? documentLibraryStore.documentsIn(effectiveSelection) : documents.slice();
  const expandedSearch = expandQuery(search);
  const visible = organised ? inSelection.filter((d) => matchesSearch(d, expandedSearch)) : inSelection;
  const groups = organised ? groupDocuments(visible) : [{ label: null, docs: visible }];

  // "Murphy is reading your documents: 12 of 37 ready." counts the batch the
  // curator just added (documents created since the bulk add began).
  const since = documentLibraryStore.batchStartedAt;
  const batchReady = since ? documents.filter((d) => d.isReady && d.createdAt && d.createdAt >= since).length : 0;
  const statusText = busyCount === 0 ? null
    : (since && batchReady > 0)
      ? `Murphy is reading your documents: ${batchReady} of ${busyCount + batchReady} ready. They become ready a few at a time.`
      : `Murphy is reading ${busyCount === 1 ? 'a document' : `${busyCount} documents`}. They become ready a few at a time.`;

  const renderCard = (doc) => (
    <Card key={doc.documentId} data-testid={`library-doc-${doc.documentId}`}>
      <DocRow>
        <DocMain>
          <DocTitleRow>
            {selectMode && (
              <SelectCheckbox
                type="checkbox"
                aria-label={`Select ${doc.title || doc.fileName}`}
                checked={selectedIds.has(doc.documentId)}
                onChange={() => toggleSelected(doc.documentId)}
              />
            )}
            <CardTitle>{doc.title || doc.fileName}</CardTitle>
            {doc.documentType && <Badge>{doc.documentType}</Badge>}
            <StatusBadge $status={doc.status}>{STATUS_LABELS[doc.status] || doc.status}</StatusBadge>
          </DocTitleRow>
          {doc.documentSummary && <DocSummary>{doc.documentSummary}</DocSummary>}
          {doc.hasError && doc.error && <ErrorText>{doc.error}</ErrorText>}
          <DocMeta>
            {doc.fileName}{formatSize(doc.size) ? ` · ${formatSize(doc.size)}` : ''}
            {doc.uploaderName ? ` · Added by ${doc.uploaderName}` : ''}{formatDate(doc.createdAt) ? ` on ${formatDate(doc.createdAt)}` : ''}
            {organised && effectiveSelection === 'all' && doc.folderId && documentLibraryStore.folderById(doc.folderId)
              ? ` · In ${documentLibraryStore.folderById(doc.folderId).name}` : ''}
            <br />
            {doc.myLastSessionAt
              ? `You last practiced this on ${formatDate(doc.myLastSessionAt)} (${doc.mySessionCount} ${doc.mySessionCount === 1 ? 'session' : 'sessions'})`
              : 'You have not practiced this document yet'}
            {doc.sessionCount > 0 ? ` · ${doc.sessionCount} team ${doc.sessionCount === 1 ? 'session' : 'sessions'} so far` : ''}
          </DocMeta>
        </DocMain>
        <DocActions>
          <PrimaryButton
            onClick={() => practice(doc)}
            disabled={!doc.isReady || busyId === doc.documentId}
            data-testid={`library-practice-${doc.documentId}`}
          >
            {busyId === doc.documentId ? 'Starting...' : 'Practice'}
          </PrimaryButton>
          {!fileRemoved(doc) && (
            <GhostButton onClick={() => view(doc)} disabled={doc.status === 'uploading'}>
              {opensInBrowser(doc) ? 'View document' : 'Download document'}
            </GhostButton>
          )}
          {/* A duplicate or unrelated document's file was removed server-side, so retrying can never succeed; only Remove makes sense. */}
          {canCurate && doc.hasError && !fileRemoved(doc) && (
            <GhostButton onClick={() => retry(doc)} disabled={busyId === doc.documentId}>Retry processing</GhostButton>
          )}
          {canCurate && <GhostButton onClick={() => rename(doc)}>Rename</GhostButton>}
          {canCurate && folders.length > 0 && !selectMode && (
            <MoveSelect
              value={doc.folderId || ''}
              onChange={(e) => moveOne(doc, e.target.value)}
              aria-label={`Move ${doc.title || doc.fileName} to`}
            >
              <option value="">Unfiled</option>
              {folders.map((f) => <option key={f.folderId} value={f.folderId}>{f.name}</option>)}
            </MoveSelect>
          )}
          {canCurate && <DangerButton onClick={() => remove(doc)} data-testid={`library-remove-${doc.documentId}`}>Remove</DangerButton>}
        </DocActions>
      </DocRow>
    </Card>
  );

  const renderList = () => {
    if (documents.length === 0 && !hasLoaded) {
      return !pageError && <EmptyState data-testid="library-loading">Loading documents...</EmptyState>;
    }
    if (documents.length === 0) {
      return (
        <EmptyState>
          {canCurate
            ? 'No documents yet. Add an RFP, proposal, SOW or fee schedule your reps need to know cold.'
            : 'No documents yet. A manager or company admin can add the documents your team should be able to defend.'}
        </EmptyState>
      );
    }
    if (visible.length === 0) {
      if (search.trim()) return <EmptyState>No documents match "{search.trim()}".</EmptyState>;
      return <EmptyState>{effectiveSelection === 'unfiled' ? 'Every document is in a folder.' : 'No documents in this folder yet.'}</EmptyState>;
    }
    return groups.map((group) => (
      <React.Fragment key={group.label || 'all'}>
        {group.label && (
          <GroupHeading>
            <GroupToggle type="button" onClick={() => toggleGroup(group.label)} aria-expanded={!collapsed.has(group.label)}>
              <span aria-hidden="true">{collapsed.has(group.label) ? '▸' : '▾'}</span>
              {group.label} ({group.docs.length})
            </GroupToggle>
          </GroupHeading>
        )}
        {!collapsed.has(group.label) && group.docs.map(renderCard)}
      </React.Fragment>
    ));
  };

  const railItems = [
    { key: 'all', label: 'All documents', count: documents.length },
    ...(showUnfiled ? [{ key: 'unfiled', label: unfiledLabel, count: unfiledCount }] : []),
    ...folders.map((f) => ({ key: f.folderId, label: f.name, count: documentLibraryStore.folderCount(f.folderId), folder: f })),
  ];

  const body = (
    <Content>
      {organised && (
        <Toolbar>
          <SearchInput
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search titles and summaries"
            aria-label="Search documents"
          />
          {canCurate && documents.length > 0 && (
            <GhostButton type="button" onClick={() => { setSelectMode((on) => !on); setSelectedIds(new Set()); }}>
              {selectMode ? 'Cancel selection' : 'Select'}
            </GhostButton>
          )}
        </Toolbar>
      )}

      {selectMode && (
        <SelectBar role="region" aria-label="Move selected documents">
          <span>{selectedIds.size} selected</span>
          <MoveSelect value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)} aria-label="Move selected documents to">
            <option value="">Unfiled</option>
            {folders.map((f) => <option key={f.folderId} value={f.folderId}>{f.name}</option>)}
          </MoveSelect>
          <PrimaryButton type="button" onClick={moveSelected} disabled={selectedIds.size === 0}>
            Move {selectedIds.size > 0 ? selectedIds.size : ''}
          </PrimaryButton>
        </SelectBar>
      )}

      {statusText && <StatusLine role="status">{statusText}</StatusLine>}

      {renderList()}
    </Content>
  );

  return (
    <PageWrapper data-tour="library-page">
      <PageHeader>
        <PageTitle>Document Library</PageTitle>
        <HeaderActions>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569' }}>
            Questions per session
            <QuestionCountSelect value={questionCount} onChange={(e) => setQuestionCount(Number(e.target.value))} aria-label="Questions per session">
              {[3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
            </QuestionCountSelect>
          </label>
          {canCurate && <GhostButton type="button" onClick={newFolder}>New folder</GhostButton>}
          {canCurate && <PrimaryButton onClick={() => setShowAdd(true)} data-testid="library-add">+ Add documents</PrimaryButton>}
        </HeaderActions>
      </PageHeader>
      <PageSubtitle>
        Documents your team presents and defends, kept here so everyone can practice on them again and again.
        Murphy asks different questions each time you practice a document, and grades you the same way as Document Defense.
      </PageSubtitle>

      {pageError && <ErrorBanner role="alert">{pageError}</ErrorBanner>}

      {organised ? (
        <Layout>
          <Rail aria-label="Folders">
            {railItems.map((item) => (
              <React.Fragment key={item.key}>
                <RailItem
                  type="button"
                  $active={effectiveSelection === item.key}
                  aria-current={effectiveSelection === item.key ? 'true' : undefined}
                  onClick={() => { setSelection(item.key); setSelectedIds(new Set()); }}
                >
                  <span>{item.label}</span>
                  <RailCount>{item.count}</RailCount>
                </RailItem>
                {canCurate && item.folder && effectiveSelection === item.key && (
                  <RailActions>
                    <GhostButton type="button" onClick={() => renameFolder(item.folder)} style={{ padding: '4px 10px' }}>Rename folder</GhostButton>
                    <DangerButton type="button" onClick={() => deleteFolder(item.folder)} style={{ padding: '4px 10px' }}>Delete folder</DangerButton>
                  </RailActions>
                )}
              </React.Fragment>
            ))}
          </Rail>
          {body}
        </Layout>
      ) : body}

      {!canCurate && documents.length > 0 && (
        <Notice>Want a document added or removed? Ask a manager or company admin; they curate this library for {company?.name || 'the company'}.</Notice>
      )}

      <AddDocumentModal
        open={showAdd}
        onClose={closeAdd}
        onAdd={onAdd}
        busy={documentLibraryStore.uploading}
        uploads={documentLibraryStore.uploads}
        uploadsPending={documentLibraryStore.uploadsPending}
        folders={folders}
        initialFolderId={selectedFolder ? selectedFolder.folderId : null}
        companyName={company?.name}
      />
    </PageWrapper>
  );
});

export default DocumentLibraryPage;
